import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Coins,
    Radio,
    Users,
    Cpu,
    Building2,
    ArrowRight,
    ShieldCheck,
    Lock,
    Sparkles,
} from "lucide-react";
import SovereignAnchor from "./SovereignAnchor";

const cormorant = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

/**
 * Vessel Showroom — five featured hulls forged in the Foundry.
 * Director-mandated copy. Each card has a 3D-perspective ship silhouette,
 * a Foundry Integrity badge, and a "View Blueprint" CTA → /shipyard.
 */
const VESSELS = [
    {
        id: "artisan-gold",
        ordinal: "01",
        name: "The Artisan Gold Platform",
        tagline: "Optimized for physical luxury and provisions.",
        body:
            "An e-commerce flagship hardened for premium goods, allocated drops, and provisioning rituals. Catalog mechanics, inner-orbit rewards, and Director-grade post-purchase transmissions are structural — not bolted on.",
        icon: Coins,
        accent: "hsl(45 85% 68%)",
        accentSoft: "hsl(45 85% 68% / 0.18)",
        integrity: { label: "Forge-tested", level: "GOLD STANDARD" },
        spec: ["Catalog mechanics", "Inner-orbit drops", "Allocated rituals"],
        sector: "Real Estate · Provisions",
        comingSoon: false,
    },
    {
        id: "sovereign-signal",
        ordinal: "02",
        name: "The Sovereign Signal Platform",
        tagline: "Optimized for high-frequency data and predictive analytics.",
        body:
            "A real-time intelligence site: streaming dashboards, low-latency websocket meshes, signal-density scoring, and predictive-analytics surfaces calibrated for capital and operational decisioning.",
        icon: Radio,
        accent: "hsl(153 60% 60%)",
        accentSoft: "hsl(153 50% 35% / 0.22)",
        integrity: { label: "Forge-tested", level: "GOLD STANDARD" },
        spec: ["Real-time mesh", "Predictive scoring", "Sub-100ms paths"],
        sector: "Finance · Signal",
        comingSoon: false,
    },
    {
        id: "recruiter-scout",
        ordinal: "03",
        name: "The Recruiter Scout Platform",
        tagline: "Optimized for white-label matching engines.",
        body:
            "A multi-tenant matching engine, white-labeled to your roster. Sovereign Match scouter, intake forms, dispatch boards, and the 60/30/10 covenant pre-wired. Bring the niche; the platform arrives ready to launch.",
        icon: Users,
        accent: "hsl(280 60% 70%)",
        accentSoft: "hsl(280 60% 50% / 0.22)",
        integrity: { label: "Forge-tested", level: "GOLD STANDARD" },
        spec: ["White-label", "Match scouter", "Dispatch board"],
        sector: "Entertainment · Recruitment",
        comingSoon: false,
    },
    {
        id: "tech-ops",
        ordinal: "04",
        name: "The Tech Ops Platform",
        tagline: "Optimized for remote support and cloud maintenance.",
        body:
            "Remote-support intake, ticketing covenant, cloud-maintenance runbooks, and on-demand technician dispatch. The front door for ongoing infrastructure operations across the entire fleet.",
        icon: Cpu,
        accent: "hsl(195 70% 65%)",
        accentSoft: "hsl(195 60% 45% / 0.22)",
        integrity: { label: "Forge-tested", level: "GOLD STANDARD" },
        spec: ["Remote support", "Ticketing covenant", "Cloud runbooks"],
        sector: "Security · Tech Ops",
        comingSoon: false,
    },
    {
        id: "property-foundry",
        ordinal: "05",
        name: "The Property Foundry",
        tagline: "Optimized for elite real estate auctions.",
        body:
            "An auction site for elite real estate — sealed-bid mechanics, NDA-gated investor briefs, Don Kir-signed covenants, and Sovereign Property division compliance. Charter waitlist now open.",
        icon: Building2,
        accent: "hsl(25 90% 60%)",
        accentSoft: "hsl(25 80% 45% / 0.22)",
        integrity: { label: "In commissioning", level: "FORGING" },
        spec: ["Sealed-bid", "NDA gateway", "Don Kir covenant"],
        sector: "Real Estate · Auction",
        comingSoon: true,
    },
];

export default function VesselShowroom() {
    return (
        <section
            id="vessel-showroom"
            data-testid="vessel-showroom"
            className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24"
        >
            {/* Aurora wash */}
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-[-10%] left-[10%] w-[40%] h-[55%] rounded-full blur-[140px] opacity-30 bg-[hsl(40_60%_45%/0.18)]" />
                <div className="absolute bottom-[-15%] right-[5%] w-[40%] h-[55%] rounded-full blur-[140px] opacity-25 bg-[hsl(153_50%_25%/0.22)]" />
                <div
                    className="absolute inset-0 opacity-[0.04]"
                    style={{
                        backgroundImage:
                            "linear-gradient(hsl(45 85% 68% / 0.4) 1px, transparent 1px), linear-gradient(90deg, hsl(45 85% 68% / 0.4) 1px, transparent 1px)",
                        backgroundSize: "56px 56px",
                    }}
                />
            </div>

            {/* Eyebrow rail */}
            <div className="relative z-10 flex items-center gap-4 mb-8">
                <div className="h-px flex-1 bg-[hsl(40_40%_25%/0.5)]" />
                <div className="text-[10px] uppercase tracking-[0.5em] text-[hsl(45_85%_68%)] font-mono flex items-center gap-2">
                    <SovereignAnchor size={14} /> The Site Showroom · Foundry Catalog
                </div>
                <div className="h-px flex-1 bg-[hsl(40_40%_25%/0.5)]" />
            </div>

            {/* Headline */}
            <div className="relative z-10 text-center max-w-3xl mx-auto mb-14">
                <h2
                    className="font-heading tracking-tighter leading-[0.95]"
                    style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}
                    data-testid="showroom-headline"
                >
                    Five hulls.{" "}
                    <span className="gold-gradient-text italic">One Foundry.</span>
                </h2>
                <p
                    className="mt-5 text-lg text-foreground/75"
                    style={cormorant}
                >
            "Each site is forged against the Sovereign covenant — Autonomous Provisioning, Invisible Security, Zero-Cap, Legacy Anchor."
                </p>
            </div>

            {/* Grid */}
            <div
                className="relative z-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
                data-testid="showroom-grid"
            >
                {VESSELS.map((v) => (
                    <VesselCard key={v.id} vessel={v} />
                ))}
            </div>

            {/* Footer rail */}
            <div className="relative z-10 mt-12 flex flex-wrap items-center justify-between gap-4 text-[10px] uppercase tracking-[0.4em] text-muted-foreground/70 font-mono">
                <span>Foundry catalog · revision A · 2026.02</span>
                <Link
                    to="/foundry"
                    data-testid="showroom-foundry-link"
                    className="text-[hsl(45_85%_68%)] hover:text-[hsl(45_85%_85%)] transition-colors flex items-center gap-1.5"
                >
                    Read the Foundry doctrine <ArrowRight size={11} />
                </Link>
            </div>
        </section>
    );
}

function VesselCard({ vessel }) {
    const Icon = vessel.icon;
    const [hover, setHover] = useState(false);

    return (
        <article
            data-testid={`vessel-${vessel.id}`}
            data-coming-soon={vessel.comingSoon ? "1" : "0"}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            className="relative group rounded-2xl overflow-hidden border border-[hsl(40_40%_25%/0.5)] bg-[hsl(151_55%_5%/0.78)] backdrop-blur-sm transition-all duration-300 hover:border-[var(--vessel-accent)]"
            style={{
                "--vessel-accent": vessel.accent,
                boxShadow: hover
                    ? `0 25px 60px -20px ${vessel.accent}55, 0 0 0 1px ${vessel.accent}33 inset`
                    : "0 12px 30px -15px rgba(0,0,0,0.4)",
            }}
        >
            {/* Top hairline */}
            <span
                aria-hidden="true"
                className="absolute top-0 left-0 right-0 h-px opacity-60 group-hover:opacity-100 transition-opacity"
                style={{ background: vessel.accent }}
            />

            {/* 3D vessel mockup canvas */}
            <VesselMockup vessel={vessel} hover={hover} />

            {/* Card body */}
            <div className="relative p-6 sm:p-7">
                {/* Ordinal + integrity badge */}
                <div className="flex items-start justify-between mb-4">
                    <span className="text-[10px] uppercase tracking-[0.45em] text-muted-foreground/65 font-mono">
                        Platform / {vessel.ordinal}
                    </span>
                    <IntegrityBadge integrity={vessel.integrity} accent={vessel.accent} />
                </div>

                {/* Name */}
                <h3
                    className="font-heading text-2xl sm:text-[1.7rem] tracking-tight leading-[1.1] uppercase"
                    style={{ color: vessel.accent }}
                    data-testid={`vessel-${vessel.id}-name`}
                >
                    {vessel.name}
                </h3>
                <p
                    className="mt-2 text-base text-foreground/80 leading-snug"
                    style={{ ...cormorant, fontStyle: "italic" }}
                >
                    {vessel.tagline}
                </p>

                {/* Body */}
                <p className="mt-4 text-sm text-foreground/65 leading-relaxed">
                    {vessel.body}
                </p>

                {/* Spec bar */}
                <div className="mt-5 pt-4 border-t border-[hsl(40_40%_25%/0.3)] flex flex-wrap gap-x-3 gap-y-1 text-[10px] uppercase tracking-[0.25em] font-mono text-muted-foreground/75">
                    {vessel.spec.map((s) => (
                        <span key={s} className="flex items-center gap-1.5">
                            <span
                                className="h-1 w-1 rounded-full"
                                style={{ background: vessel.accent }}
                            />
                            {s}
                        </span>
                    ))}
                </div>

                {/* CTA */}
                <div className="mt-6 flex items-center justify-between gap-3">
                    <span className="text-[9px] uppercase tracking-[0.4em] text-muted-foreground/60 font-mono">
                        {vessel.sector}
                    </span>
                    {vessel.comingSoon ? (
                        <Link
                            to="/property/foundry-listing-01"
                            data-testid={`vessel-${vessel.id}-cta`}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[hsl(40_40%_25%/0.6)] text-[11px] uppercase tracking-[0.3em] font-semibold hover:border-[var(--vessel-accent)] hover:text-[var(--vessel-accent)] transition-colors text-foreground/85"
                        >
                            <Lock size={11} /> Charter waitlist
                        </Link>
                    ) : (
                        <Link
                            to="/shipyard"
                            data-testid={`vessel-${vessel.id}-cta`}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.3em] font-semibold transition-all"
                            style={{
                                background: hover ? vessel.accent : "transparent",
                                border: `1px solid ${vessel.accent}`,
                                color: hover ? "hsl(40 20% 8%)" : vessel.accent,
                            }}
                        >
                            View blueprint <ArrowRight size={11} />
                        </Link>
                    )}
                </div>
            </div>
        </article>
    );
}

function IntegrityBadge({ integrity, accent }) {
    const isForging = integrity.level === "FORGING";
    return (
        <div
            data-testid="vessel-integrity-badge"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
            style={{
                background: "rgba(0,0,0,0.45)",
                border: `1px solid ${isForging ? "hsl(25 80% 55% / 0.5)" : accent + "55"}`,
            }}
        >
            {isForging ? (
                <Sparkles size={10} style={{ color: "hsl(25 90% 65%)" }} />
            ) : (
                <ShieldCheck size={10} style={{ color: accent }} />
            )}
            <span
                className="text-[8.5px] uppercase tracking-[0.3em] font-bold font-mono"
                style={{ color: isForging ? "hsl(25 90% 70%)" : accent }}
            >
                {integrity.level}
            </span>
        </div>
    );
}

/**
 * 3D-perspective vessel mockup. Pure SVG/CSS — a stylized hull silhouette
 * that tilts on hover, sailing across a celestial gradient with a glow trail.
 * Different "hull shapes" per vessel id to visually differentiate the 5 cards.
 */
function VesselMockup({ vessel, hover }) {
    return (
        <div
            className="relative h-44 sm:h-48 overflow-hidden border-b border-[hsl(40_40%_25%/0.3)]"
            style={{
                background: `radial-gradient(120% 80% at 50% 100%, ${vessel.accentSoft} 0%, hsl(151 55% 4%) 60%, hsl(0 0% 2%) 100%)`,
            }}
        >
            {/* Star field */}
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
                {[...Array(40)].map((_, i) => {
                    const top = (i * 37) % 100;
                    const left = (i * 53) % 100;
                    const size = (i % 3) + 1;
                    const opacity = ((i * 11) % 70) / 100 + 0.2;
                    return (
                        <span
                            key={i}
                            className="absolute rounded-full bg-[hsl(45_85%_85%)]"
                            style={{
                                top: `${top}%`,
                                left: `${left}%`,
                                width: `${size}px`,
                                height: `${size}px`,
                                opacity,
                                boxShadow: size > 1 ? `0 0 ${size * 2}px hsl(45 85% 75%)` : "none",
                            }}
                        />
                    );
                })}
            </div>

            {/* Aurora horizon */}
            <span
                aria-hidden="true"
                className="absolute bottom-0 left-0 right-0 h-12 opacity-60"
                style={{
                    background: `linear-gradient(0deg, ${vessel.accentSoft} 0%, transparent 100%)`,
                }}
            />

            {/* Glow trail behind vessel */}
            <span
                aria-hidden="true"
                className="absolute left-1/2 -translate-x-1/2 bottom-7 h-1 rounded-full transition-all duration-500"
                style={{
                    width: hover ? "60%" : "40%",
                    background: vessel.accent,
                    filter: "blur(14px)",
                    opacity: 0.55,
                }}
            />

            {/* The hull — a stylized SVG silhouette that pseudo-3D tilts on hover */}
            <div
                className="absolute inset-0 flex items-center justify-center transition-transform duration-500 ease-out"
                style={{
                    transform: hover
                        ? "perspective(700px) rotateX(8deg) rotateY(-6deg) scale(1.05) translateY(-4px)"
                        : "perspective(700px) rotateX(2deg) rotateY(0deg) scale(1)",
                }}
            >
                <HullSilhouette vessel={vessel} />
            </div>

            {/* Floating icon medallion (bottom-left) */}
            <div
                className="absolute bottom-3 left-3 h-9 w-9 rounded-full flex items-center justify-center backdrop-blur-sm"
                style={{
                    background: "rgba(0,0,0,0.5)",
                    border: `1px solid ${vessel.accent}66`,
                }}
            >
                <vessel.icon size={14} style={{ color: vessel.accent }} />
            </div>

            {/* Sector chip (top-right) */}
            {vessel.comingSoon && (
                <div
                    className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.35em] font-bold font-mono"
                    style={{
                        background: "rgba(0,0,0,0.55)",
                        border: "1px solid hsl(25 80% 55% / 0.45)",
                        color: "hsl(25 90% 70%)",
                    }}
                >
                    Coming Soon
                </div>
            )}
        </div>
    );
}

/**
 * Hull silhouettes — distinct SVG shape per vessel for visual variety.
 * Each is rendered with a gold/accent gradient + subtle reflection lower-glow.
 */
function HullSilhouette({ vessel }) {
    const a = vessel.accent;
    const id = `grad-${vessel.id}`;
    return (
        <svg
            width="180"
            height="100"
            viewBox="0 0 180 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="drop-shadow-[0_8px_22px_rgba(0,0,0,0.55)]"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={a} stopOpacity="0.95" />
                    <stop offset="55%" stopColor={a} stopOpacity="0.55" />
                    <stop offset="100%" stopColor={a} stopOpacity="0.18" />
                </linearGradient>
            </defs>

            {/* Different hull shape per vessel id */}
            {vessel.id === "artisan-gold" && (
                <>
                    <path
                        d="M20 65 Q90 95 160 65 L150 75 Q90 100 30 75 Z"
                        fill={`url(#${id})`}
                    />
                    <path d="M88 18 L92 18 L92 60 L88 60 Z" fill={a} opacity="0.85" />
                    <path d="M92 22 L130 40 L92 42 Z" fill={a} opacity="0.65" />
                    <path d="M88 24 L52 40 L88 42 Z" fill={a} opacity="0.55" />
                </>
            )}
            {vessel.id === "sovereign-signal" && (
                <>
                    <path
                        d="M15 60 L165 60 L150 80 L30 80 Z"
                        fill={`url(#${id})`}
                    />
                    <path d="M50 60 L60 35 L70 60 Z" fill={a} opacity="0.8" />
                    <path d="M85 60 L95 25 L105 60 Z" fill={a} opacity="0.9" />
                    <path d="M115 60 L122 40 L130 60 Z" fill={a} opacity="0.7" />
                    <circle cx="95" cy="22" r="3" fill={a} />
                </>
            )}
            {vessel.id === "recruiter-scout" && (
                <>
                    <path
                        d="M20 60 Q40 50 90 50 Q140 50 160 60 L150 80 L30 80 Z"
                        fill={`url(#${id})`}
                    />
                    <circle cx="60" cy="40" r="6" fill={a} opacity="0.85" />
                    <circle cx="90" cy="32" r="8" fill={a} />
                    <circle cx="120" cy="40" r="6" fill={a} opacity="0.85" />
                    <line x1="60" y1="40" x2="90" y2="32" stroke={a} strokeWidth="1" opacity="0.5" />
                    <line x1="120" y1="40" x2="90" y2="32" stroke={a} strokeWidth="1" opacity="0.5" />
                </>
            )}
            {vessel.id === "tech-ops" && (
                <>
                    <rect x="25" y="50" width="130" height="20" fill={`url(#${id})`} />
                    <rect x="35" y="35" width="110" height="15" fill={a} opacity="0.65" />
                    <rect x="60" y="40" width="6" height="6" fill={a} opacity="0.95" />
                    <rect x="75" y="40" width="6" height="6" fill={a} opacity="0.85" />
                    <rect x="90" y="40" width="6" height="6" fill={a} opacity="0.95" />
                    <rect x="105" y="40" width="6" height="6" fill={a} opacity="0.65" />
                    <path d="M25 70 L40 80 L140 80 L155 70 Z" fill={a} opacity="0.5" />
                </>
            )}
            {vessel.id === "property-foundry" && (
                <>
                    <path
                        d="M40 75 L40 50 L65 50 L65 35 L115 35 L115 50 L140 50 L140 75 Z"
                        fill={`url(#${id})`}
                    />
                    <path d="M75 75 L75 60 L105 60 L105 75 Z" fill="hsl(151 55% 4%)" />
                    <path d="M55 65 L55 55 L65 55 L65 65 Z" fill={a} opacity="0.6" />
                    <path d="M115 65 L115 55 L125 55 L125 65 Z" fill={a} opacity="0.6" />
                    <path d="M85 25 L85 35 L95 35 L95 25 Z" fill={a} opacity="0.85" />
                </>
            )}
            {/* Reflection */}
            <path
                d="M30 82 Q90 92 150 82"
                stroke={a}
                strokeOpacity="0.35"
                strokeWidth="1"
                fill="none"
            />
        </svg>
    );
}
